import Alert from 'react-s-alert';
import {
  observable,
  action,
  runInAction,
  toJS,
  autorun,
  computed,
  reaction,
} from 'mobx';
import { isNil, omitBy } from 'lodash';
import { AppManager, Input, routerStore } from 'core';

import {
  gutHashFile,
  gutUploadFiles,
  isInDesktopMode,
  createTab,
  getCurrentTabIdFromDesktop,
} from 'libs/gutClient/electronBridge';

import { StatusType, FileMeta } from 'libs/gutClient/types';
import {
  BulkFileMutation,
  BulkCompleteFileMutation,
  CheckinStageMutation,
  ResetStageMutation,
} from 'apps/file/gql';
import { analytics } from 'core/analytics';
import { clientStore } from 'apps/client/app';
import { parallelWaterfall } from 'libs/parallelWaterfall';
import { isInitiativeSlug } from 'utils';
import uuidV4 from 'uuid';
import {
  removeLocalStorage,
  fromLocalStorage,
  toLocalStorage,
} from 'core/localStorage';
import type { DriveTableDataSource } from '@wikifactory/voxel-ui';
import {
  getPathContentsFromTree,
  mapChangeTypeGQLOpType,
  batchPromiseAll,
  splitArrayInBatches,
  getLastSegment,
  pathCompare,
} from './utils';
import type { SyncRepoAppManager } from './appManager';
import { CheckinContributionMutation } from '../gql';
import { ContributionApp } from '../app';
import { navigateToFile } from './SyncProjectContainer/navigateUtils';

export type ContributionItem = {
  path: string;
  type: StatusType;
  workingDirMeta: FileMeta;
  hasUploadBeenInitiated: boolean;
  hasBeenStaged: boolean;
  fileId?: string;
  fullPath?: string;
  signedUrl?: string;
  uploadedHash?: string;
};

type ContributionState = {
  uuid: string;
  items: ContributionItem[];
  title: string;
  description: string;
};

const BATCH_SIZE = 50;
const CONTRIBUTION_STATE_STORAGE_KEY = 'contributionState';

export class ContributingManager extends AppManager {
  static registryName = 'ContributingManager';

  syncRepoManager: SyncRepoAppManager = null;

  @observable contributionState: ContributionState;
  @observable pendingContributionState: ContributionState;

  @observable checkingIn = false;
  @observable isContributing = false;

  @observable contributionErrorBannerMessage = '';

  constructor(parent: SyncRepoAppManager, init = {}, options = {}) {
    super(parent, init, options);
    this.syncRepoManager = parent;
  }

  init = () => {
    this.LoadContributionFromStorage();
    this.initReactions();
  };

  initReactions = () => {
    this.initSavePendingContributionToStorageReaction();
    this.initCancelPendingOnLocalChangesMismatchReaction();
  };

  initSavePendingContributionToStorageReaction = () => {
    // 1 - Stores an ongoing contribution state (this.contributionState)
    // So it can be resumed on unexpected interruption (e.g. shutdown)
    // 2 - Stores contributions that have errored (this.pendingContributionState)
    // and we know can be resumed so that it survives closing-open the app
    // 3- When both of those states are null it clears the storage.
    // That way it's unnecessary for any other code to clear the storage, it
    // just needs to clear variables as normal.

    this.disposers.push(
      autorun(
        () => {
          if (!this.syncRepoManager.repoId) return;
          let stateToSave = this.contributionState;
          if (!stateToSave && this.pendingContributionState) {
            stateToSave = this.pendingContributionState;
          }
          if (!stateToSave) {
            removeLocalStorage(this.getContributionStorageKey());
          } else {
            toLocalStorage(
              this.getContributionStorageKey(),
              toJS(stateToSave),
              true,
            );
          }
        },
        { delay: 100 },
      ),
    );
  };

  initCancelPendingOnLocalChangesMismatchReaction = () => {
    // clears the pending contribution if the local changes operations
    // types no longer match with the pending changes.
    this.disposers.push(
      reaction(
        () => this.syncRepoManager.selectableLocalChangesTableRowsMap,
        localChangesMap => {
          if (!this.syncRepoManager.statusHasBeenLoaded) return;
          if (!this.pendingContributionState || !localChangesMap) return;

          let shouldResetPendingContribution = false;
          for (let i = 0; i < this.pendingContributionState.items.length; ++i) {
            const item = this.pendingContributionState.items[i];
            const matchingChange = localChangesMap[item.path];
            if (!matchingChange || matchingChange.type !== item.type) {
              shouldResetPendingContribution = true;
              break;
            }
          }
          if (shouldResetPendingContribution) {
            this.pendingContributionState = null;
          }
        },
      ),
    );
  };

  getContributionStorageKey = () => {
    return `${CONTRIBUTION_STATE_STORAGE_KEY}-${this.syncRepoManager.repoId}`;
  };

  @action LoadContributionFromStorage = () => {
    this.pendingContributionState = fromLocalStorage(
      this.getContributionStorageKey(),
    );
  };

  @computed get contributionItemsMap() {
    const obj: Record<string, ContributionItem> = {};
    this.contributionState?.items.forEach(item => {
      obj[item.path] = item;
    });
    return obj;
  }

  @computed get canResumeContribution() {
    return Boolean(this.pendingContributionState);
  }

  @action CancelPendingContribution = () => {
    if (!this.pendingContributionState) return;

    /* await */ this.resetStagingContribution(
      this.pendingContributionState.uuid,
    ).catch(() => {
      /* we do not care, could inform */
    });

    this.pendingContributionState = null;
  };

  @action Resume = async () => {
    const contributionState = this.pendingContributionState;
    this.pendingContributionState = null;

    /* const success = */ await this.DoContribute(contributionState, true);
  };

  @action Contribute = async (title: string, description = '') => {
    // this.changesToContribute = selectedLocalChanges;

    if (!title) {
      Alert.error('Please add a title to your contribution.');
      // return await Promise.resolve(false);
      return;
    }

    const selectedLocalChanges = [
      ...this.syncRepoManager.selectedContributableLocalChanges,
    ].sort((a, b) => pathCompare(a.path, b.path));

    if (selectedLocalChanges.length === 0) {
      Alert.error('Please select some changes to contribute.');
      // return await Promise.resolve(false);
      return;
    }

    // technically unnecessary, but should be fast
    await this.syncRepoManager.LoadHeadHash();

    const hasConflicts = selectedLocalChanges.some(
      localChange => localChange.type === StatusType.Conflicted,
    );
    if (hasConflicts) {
      Alert.error(
        'You can not contribute conflicted files. Please resolve the conflicts first.',
      );
      // return await Promise.resolve(false);
      return;
    }

    const contributionUuid = uuidV4();

    const contributionItems: ContributionItem[] = selectedLocalChanges.map(
      change => ({
        type: change.type,
        path: change.path,
        fullPath: `${this.syncRepoManager.connectedPath}/${change.path}`,
        workingDirMeta: { ...change.workingDirMeta },
        hasUploadBeenInitiated: false,
        hasBeenStaged: false,
      }),
    );

    const contributionState: ContributionState = {
      uuid: contributionUuid,
      items: contributionItems,
      title,
      description,
    };

    this.CancelPendingContribution();

    /* const success = */ await this.DoContribute(contributionState);
  };

  hashingTask = async (contributionItems: ContributionItem[]) => {
    // Mostly the changes that are missing a hash are ADDED files
    // because gut does not care about it initially.
    // However with the resume system it's no longer a strong equivalence we
    // could have ADDED files with a hash because it was loaded from storage
    const changesWithoutHash = contributionItems.filter(
      localChange =>
        !localChange.workingDirMeta.hash &&
        localChange.type !== StatusType.Deleted,
    );

    const hashesArray = await this.getHashesOfFiles(changesWithoutHash);
    runInAction(() => {
      hashesArray.forEach((hash, i) => {
        // NOTE: Alternatively create another hash key outside of workingDirMeta
        // to signify the different source, and getting the hash of a file
        // would require checking both entries
        changesWithoutHash[i].workingDirMeta.hash = hash;
      });
    });

    return {
      contributionItems,
    };
  };

  fileObjectsTask = async ({
    contributionItems,
  }: {
    contributionItems: ContributionItem[];
  }) => {
    const changesThatNeedFileObjects = contributionItems.filter(
      item =>
        (item.type === StatusType.Added ||
          item.type === StatusType.Modified ||
          // this is to upload and view local conflicts
          item.type === StatusType.Conflicted) &&
        !item.fileId, // could already be there because of resuming
    );
    const batchedChangesPromiseAll = batchPromiseAll(
      changesThatNeedFileObjects,
      BATCH_SIZE,
    );
    const remoteFileObjects = await batchedChangesPromiseAll(
      this.createRemoteFileObjects,
    );

    runInAction(() => {
      remoteFileObjects.forEach((remoteFile: any, index: number) => {
        // affects contributionItems because it's the same reference
        const matchingChange = changesThatNeedFileObjects[index];
        matchingChange.fileId = remoteFile.id;
        if (!remoteFile.completed) {
          matchingChange.signedUrl = remoteFile.uploadUrl;
        } else {
          matchingChange.signedUrl = null;
        }
      });
    });

    return {
      contributionItems,
    };
  };

  uploadTask = async ({
    contributionItems,
  }: {
    contributionItems: ContributionItem[];
  }) => {
    const toUpload = contributionItems.filter(
      change =>
        change.signedUrl && // needs to be uploaded because we got an url
        !change.uploadedHash, // could already have been uploaded because of resuming
    );

    const uploadedHashes = await this.uploadFiles(toUpload);

    runInAction(() => {
      uploadedHashes.forEach((uploadedHash, index) => {
        // this affects contributionItems because it's the same reference
        toUpload[index].uploadedHash = uploadedHash;

        // Generally the next line is unnecessary, the listener on the bridge
        // will set this up. But it's a failsafe that makes sense
        // because if there's an internet interruption the uploadedHash would get
        // updated because it modifies the pendingContributionState which gets written
        // but the corresponding hasUploadBeenInitiated would not happen because
        // the listenToUploads does not attempt to modify pendingContributionState
        toUpload[index].hasUploadBeenInitiated = true;
      });
    });

    return { contributionItems };
  };

  completeFilesTask = async ({
    contributionItems,
  }: {
    contributionItems: ContributionItem[];
  }) => {
    const batchedUploadsPromiseAll = batchPromiseAll(
      contributionItems,
      BATCH_SIZE,
    );
    await batchedUploadsPromiseAll(this.completeFiles);
    return { contributionItems };
  };

  stageOperations = async (
    contributionItems: ContributionItem[],
    contributionUuid: string,
  ) => {
    const operations = contributionItems.map(item => ({
      projectId: this.syncRepoManager.repoId,
      opType: mapChangeTypeGQLOpType(item.type),
      fileId: item.fileId,
      path: item.path,
      oldPath: null as string,
      gitHash: item.uploadedHash,
    }));

    const input = new Input({
      checkinStageInput: {
        projectId: this.syncRepoManager.repoId,
        parent: this.syncRepoManager.headHash || null, // null for first commit of the repo
        operations,
        stagingUuid: contributionUuid,
      },
      fileSync: true,
      stagingUuid: contributionUuid,
    });

    const checkingStageMutation = new CheckinStageMutation(
      input,
      null,
      {},
      true,
    );

    return new Promise((resolve, reject) => {
      checkingStageMutation.mutate({
        onSuccess: (response: any) => {
          if (response === 'ok') {
            runInAction(() => {
              contributionItems.forEach(item => {
                // eslint-disable-next-line no-param-reassign
                item.hasBeenStaged = true;
              });
            });
          }
          resolve(response);
        },
        onUserErr: reject,
        onSysErr: reject,
      });
    });
  };

  stagingTask = async (
    { contributionItems }: { contributionItems: ContributionItem[] },
    contributionUuid: string,
  ) => {
    const contributionItemsToStage = contributionItems.filter(
      item => !item.hasBeenStaged,
    );
    const batchedStagePromiseAll = batchPromiseAll(
      contributionItemsToStage,
      BATCH_SIZE,
    );
    await batchedStagePromiseAll(this.stageOperations, contributionUuid);
  };

  @action DoContribute = async (
    contributionState: ContributionState,
    skipResetingStagingArea = false,
  ) => {
    let debugStep = 0;
    try {
      this.isContributing = true;
      // this.uploadingJob = {};
      // this.changesToContribute = [];
      this.contributionErrorBannerMessage = '';

      // this.changesToContribute = selectedLocalChanges;

      this.contributionState = contributionState;

      const { uuid } = this.contributionState;
      debugStep += 1;
      if (!skipResetingStagingArea) {
        await this.resetStagingContribution(uuid);
      }

      debugStep += 1;
      const success = await this.CommitToStagingArea(
        uuid,
        this.contributionState.items,
      );
      debugStep += 1;
      if (!success) return;

      debugStep += 1;
      const checkinSuccess = await this.Checkin(
        contributionState.uuid,
        contributionState.title,
        contributionState.description,
      );
      debugStep += 1;
      if (!checkinSuccess) {
        this.pendingContributionState = this.contributionState;
        return;
      }
      debugStep += 1;
      this.pendingContributionState = null;
      this.contributionState = null;
    } catch (error) {
      analytics.logError(error);
      try {
        const contributionStateJson = JSON.stringify(
          toJS(this.contributionState),
        );
        analytics.logError(
          new Error(
            `Contribution failed (${debugStep}) with ${contributionStateJson}`,
          ),
        );
      } catch (e) {
        analytics.logError(
          new Error(`Debug error handling failed ${e?.message}`),
        );
      }
      if (
        error.extensions?.code === 'BAD_REQUEST' &&
        error.message.includes('Expected parent')
      ) {
        if (error.message.includes('to be equal to staging parent')) {
          // This error happens when we resume an old staging area but
          // there is a new parent
          const msg =
            'The contribution is outdated, please sync and create a new contribution.';
          this.contributionErrorBannerMessage = msg;
          this.CancelPendingContribution();
          Alert.error(msg);
          return;
        }
        const msg =
          'There are newer contributions in the Cloud Drive, please pull cloud changes before contributing.';
        Alert.error(msg);
        // this.contributionErrorBannerMessage = msg;

        this.syncRepoManager.QueryLatestContribution();
        return;
      }

      if (error.extensions?.code === 'VALIDATION') {
        let msg = `The contribution could not be validated: ${error.message}`;
        if (error.message.toLowerCase().includes('illegal characters')) {
          msg =
            'The contribution could not be completed because some files or folders names contain illegal characters: * "  < > : | ?';
        } else if (error.message.toLowerCase().includes('control characters')) {
          msg =
            'The contribution could not be completed because some files or folders names contain illegal control characters.';
        }
        this.contributionErrorBannerMessage = msg;
        Alert.error(msg);
        return;
      }

      // NOTE(david): this.pendingContributionState having data will show the Resume banner
      // The previous error cases returning prevent the resume functionality from happening
      this.pendingContributionState = this.contributionState;

      // Unnecessary as this is now contained in the Resume banner
      // this.contributionErrorBannerMessage = 'Your last contribution was interrupted and could not be completed.';

      Alert.error(
        'Sorry, there was an error sending the contribution. This has been notified.',
      );
    } finally {
      runInAction(() => {
        this.contributionState = null;
        this.isContributing = false;
        this.checkingIn = false;
      });
    }
  };

  @action CommitToStagingArea = async (
    contributionUuid: string,
    contributionItems: ContributionItem[],
  ) => {
    const tasks = [
      this.hashingTask,
      this.fileObjectsTask,
      this.uploadTask,
      // this.completeFilesTask,
      {
        task: this.stagingTask,
        options: { extraArgs: [contributionUuid] },
      },
    ];

    // there is no particular requirement these sizes must match
    const WATERFALL_BATCH_SIZE = BATCH_SIZE;
    const dataArrays = splitArrayInBatches(
      contributionItems,
      WATERFALL_BATCH_SIZE,
    );

    try {
      await parallelWaterfall(dataArrays, tasks);
    } catch (error) {
      // const handled = handleCheckinErrors(error);
      // if (handled) return Promise.resolve(false);
      throw error;
    }

    return true;
  };

  @action Diff = async (change: any) => {
    if (change.type !== 'Modified' && change.type !== 'Conflicted') {
      return;
    }

    const contributionItems: ContributionItem[] = [
      {
        type: change.type,
        path: change.path,
        fullPath: `${this.syncRepoManager.connectedPath}/${change.path}`,
        workingDirMeta: { ...change.workingDirMeta },
        hasUploadBeenInitiated: false,
        hasBeenStaged: false,
      },
    ];

    const contributionState: ContributionState = {
      uuid: null,
      items: contributionItems,
      title: '',
      description: '',
    };

    const treeContents = getPathContentsFromTree(
      this.syncRepoManager.remoteTree,
      change.path,
    );
    if (!treeContents || treeContents.length === 0) {
      // TODO this can be avoided if the basepath of the link that opens is for the current
      // point in time
      analytics.logError(
        new Error(
          `Attempted to diff modifications at ${change.path} but cloud drive does not have it.`,
        ),
      );
      Alert.error('The file no longer exists in the cloud drive.');
      return;
    }

    const uplodedItems = await this.doUploadChanges(contributionState);
    await this.navigateToFileObject(uplodedItems[0].contributionItems[0], true);
  };

  @action ViewLocalFile = async (change: any) => {
    if (
      change.type === 'NoChange' /* NoChange is locked */ ||
      change.type === 'Deleted'
    ) {
      const { path } = change;
      const filename = getLastSegment(path);
      const hash = this.syncRepoManager.headHash;
      const projectName = this.syncRepoManager.repoManager.parent.project.name;
      navigateToFile(path, filename, hash, projectName);
      // eslint-disable-next-line no-useless-return
      return;
    }

    // change is: Added, Modified, Conflicted
    /*
    // TODO: The following code works but the UI at the other side
    // of the navigation needs work.
    // We need some sort of route /localFile/<id>
    // Because I think having a 
    // <projectslug>/file/potentiallyNonExistingFolders/localfile.ext
    // Is problematic and would require a lot of work.
    //
    // Also might want to use a contribDiff=<contribFileId> poaram in the future.

    const contributionItems: ContributionItem[] = [
      {
        type: change.type,
        path: change.path,
        fullPath: `${this.syncRepoManager.connectedPath}/${change.path}`,
        workingDirMeta: { ...change.workingDirMeta },
        hasUploadBeenInitiated: false,
        hasBeenStaged: false,
      },
    ];

    const contributionState: ContributionState = {
      uuid: null,
      items: contributionItems,
      title: '',
      description: '',
    };

    const res = await this.doUploadChanges(contributionState);
    await this.navigateToFileObject(res[0].contributionItems[0]);
    */
  };

  navigateToDiffTask = async ({
    contributionItems,
  }: {
    contributionItems: ContributionItem[];
  }) => {
    const {
      currentSpaceData,
      flags: profileFlag,
      slugFromRouteOrSpace,
    } = clientStore;

    const shouldShowDiffOrMeasurement = isInitiativeSlug(slugFromRouteOrSpace)
      ? JSON.parse(currentSpaceData?.flags || '{}').cadDiff
      : (profileFlag as { cadDiff: boolean })?.cadDiff;

    const tabId = await getCurrentTabIdFromDesktop();

    // NOTE(david): This is just a short-sighted copy paste
    // This is a forEach loop and in it we navigate which makes no sense
    // This works because contributionItems.length === 1
    contributionItems.forEach((item: ContributionItem) => {
      const isConflict = item.type === StatusType.Conflicted;
      const params = omitBy(
        {
          ...routerStore.route.params,
          basepath: item.path || null,
          diff: shouldShowDiffOrMeasurement
            ? window.encodeURIComponent(item.fileId)
            : null,
          repoId: shouldShowDiffOrMeasurement
            ? this.syncRepoManager.repoId
            : null,
          tabId,
          conflict: isConflict || null,
        },
        isNil,
      );

      const toRouteName = `${clientStore.spaceRoute.route}.file`;

      if (isInDesktopMode()) {
        const toPath = routerStore.router.buildUrl(toRouteName, params);

        createTab(
          `${window.location.href}${toPath}`,
          getLastSegment(item.path),
          shouldShowDiffOrMeasurement ? 'comparison' : 'file',
          {
            routeName: toRouteName,
            options: params,
          },
          {
            uniqueFlag: item.path,
          },
        );

        return;
      }

      routerStore.navigate(toRouteName, params);
    });

    return {
      contributionItems,
    };
  };

  navigateToFileObject = async (item: ContributionItem, isDiff = false) => {
    // TODO: this is incorrect, we want our tab, not the currently open tab
    // we should send null and then the other side of the bridge should take
    // the id of the sender of the event, which is readily avilable there.
    const tabId = await getCurrentTabIdFromDesktop();

    const isConflict = item.type === StatusType.Conflicted;
    const params = omitBy(
      {
        ...routerStore.route.params,
        basepath: item.path || null,
        diff: isDiff ? window.encodeURIComponent(item.fileId) : null,
        repoId: isDiff ? this.syncRepoManager.repoId : null,
        tabId,
        conflict: (isDiff && isConflict) || null,
      },
      isNil,
    );

    const toRouteName = `${clientStore.spaceRoute.route}.file`;

    if (isInDesktopMode()) {
      const toPath = routerStore.router.buildUrl(toRouteName, params);

      createTab(
        `${window.location.href}${toPath}`,
        getLastSegment(item.path),
        isDiff ? 'comparison' : 'file',
        {
          routeName: toRouteName,
          options: params,
        },
        {
          uniqueFlag: item.path,
        },
      );

      return;
    }

    routerStore.navigate(toRouteName, params);
  };

  doUploadChanges = async (contributionState: ContributionState) => {
    const tasks = [this.hashingTask, this.fileObjectsTask, this.uploadTask]; //

    const dataArrays = [contributionState.items];

    try {
      return await parallelWaterfall(dataArrays, tasks);
    } catch (error) {
      // handleOnDemandConversionErrors and others
      analytics.logError(error);
      throw error;
    }
  };

  resetStagingContribution = async (contributionUuid: string) => {
    const resetStagingInput = {
      projectId: this.syncRepoManager.repoId,
      fileSync: true,
      stagingUuid: contributionUuid,
    };
    const resetStageMutation = new ResetStageMutation(
      resetStagingInput,
      null,
      {},
      false,
    );
    return new Promise((resolve, reject) => {
      resetStageMutation.mutate({
        onSuccess: resolve,
        onSysErr: reject,
        onUserErr: reject,
      });
    });
  };

  getHashesOfFiles = async (changesToHash: ContributionItem[]) => {
    const hashesArray = await Promise.all(
      changesToHash.map(localChange => {
        return gutHashFile(this.syncRepoManager.repoId, localChange.path);
      }),
    );
    return hashesArray;
  };

  createRemoteFileObjects = async (
    operations: ContributionItem[],
    temporary?: boolean,
  ) => {
    const addFileInputs = operations.map(operation => {
      const file = operation;

      // const targetSpaceId = clientStore.signedInProfile?.inSpace.id; // NOTE: in uploader this can be something else
      const targetSpaceId =
        this.syncRepoManager.repoManager.parent.project?.inSpace.id ||
        this.syncRepoManager.repoManager.repository?.inSpace.id;

      const gitHash = file.workingDirMeta.hash;
      if (!gitHash) throw new Error(`Missing hash for file: ${file.path}`);

      const addFileInput = {
        filename: getLastSegment(file.path),
        // private: file.private,
        spaceId: targetSpaceId, // TODO what is this
        contentType: 'application/octet-stream',
        size: file.workingDirMeta.size,
        gitHash,
        fileLastModified: new Date(file.workingDirMeta.mtime).toISOString(),
        projectPath: file.path,
        temporary,
      };

      // const headers = {};
      // if (!clientStore.signedInProfile && clientStore.HMAC) {
      //   headers['X-Wikifactory-Hmac'] = clientStore.HMAC;
      // }
      // fileMutation.addHeaders(headers);
      return addFileInput;
    });

    const bulkFileMutation = new BulkFileMutation(addFileInputs);
    return new Promise((resolve, reject) => {
      bulkFileMutation.setCallbacks({
        onSuccess: resolve,
        onSysErr: reject,
        onUserErr: reject,
      });
      bulkFileMutation.mutate();
    });
  };

  uploadFiles = async (uploads: ContributionItem[]) => {
    if (uploads.length === 0) return [];
    const plainJsUploads = uploads.map(upload => ({
      filePath: upload.fullPath,
      signedUrl: upload.signedUrl,
    }));
    const [uploaded, uploadError] = await gutUploadFiles(
      this.syncRepoManager.repoId,
      plainJsUploads,
    );
    if (uploadError) throw uploadError;
    return uploaded;
  };

  completeFiles = async (contributionItems: ContributionItem[]) => {
    const bulkCompleteFilesInput = contributionItems.map(item => {
      const input = {
        id: item.fileId,
        completed: true,
      } as { id: string; completed: boolean; gitHash?: string };
      if (item.uploadedHash) input.gitHash = item.uploadedHash;
      return input;
    });

    const headers = {};
    // if (!clientStore.signedInProfile && clientStore.HMAC) {
    //   headers['X-Wikifactory-Hmac'] = clientStore.HMAC;
    // }
    const fileMutation = new BulkCompleteFileMutation(
      bulkCompleteFilesInput,
      null,
      {},
      false,
      headers,
    );
    return new Promise((resolve, reject) => {
      fileMutation.mutate({
        onSuccess: (arg: any) => {
          resolve(arg);
        },
        onUserErr: reject,
        onSysErr: reject,
      });
    });
  };

  @action Checkin = async (
    contributionUuid: string,
    title: string,
    description = '',
  ) => {
    this.checkingIn = true;

    const checkinInput = new Input({
      checkinData: {
        projectId: this.syncRepoManager.repoId,
        title,
        description,
        parent: this.syncRepoManager.headHash || null, // null for first commit of the repo
        stagingUuid: contributionUuid,
      },
    });

    const handleCheckinErrors = (error: any) => {
      if (error.extensions?.code === 'LOCKED_FILES') {
        analytics.logError(error);

        const gqlError = error;
        let filesText = '';
        const filesData = gqlError.extensions.locked_paths;
        if (filesData) filesText = Object.keys(filesData).join(', ');
        const msgLength = 40;
        if (filesText.length > msgLength)
          filesText = `${filesText.substring(0, msgLength - 3)}...`;

        Alert.error(
          `Can't contribute because some files are locked: ${filesText}`,
        );
        this.syncRepoManager.lockingManager.QueryLockedFiles();
        return true;
      }

      if (
        error.extensions?.code === 'BAD_REQUEST' &&
        error.message.includes('Expected parent')
      ) {
        analytics.logError(error);

        Alert.error(
          'There are newer contributions in the Cloud Drive, please pull cloud changes before contributing.',
        );
        this.syncRepoManager.QueryLatestContribution();
        return true;
      }
      return false;
    };

    return new Promise((resolve, reject) => {
      const handleContributionError = (error: any) => {
        const handled = handleCheckinErrors(error);
        if (handled) resolve(false);
        else reject(error);
      };

      const checkInMutation = new CheckinContributionMutation(
        checkinInput,
        null,
        {
          onSuccess: async (result: any) => {
            this.syncRepoManager.previousHeadHash =
              this.syncRepoManager.headHash;
            this.syncRepoManager.justCommitedHash = result.hash;
            try {
              resolve(true);
              // update history
              this.syncRepoManager.repoManager.contributionsConn?.Reset();
              this.syncRepoManager.repoManager.contributionsConn?.loadMore();

              // if (numAddedOrModifiedChanges > 0) {
              // update the commit messages for current page
              this.syncRepoManager.repoManager.NewRepoQuery();
              // }
              await this.syncRepoManager.PullAndSync();
            } finally {
              this.syncRepoManager.justCommitedHash = null;
            }
          },
          onUserErr: handleContributionError,
          onSysErr: handleContributionError,
        },
        true,
      );
      checkInMutation.mutate();
    });
  };

  @action ContributeRestoreFileToVersion = async (
    file: DriveTableDataSource,
    contribution: ContributionApp,
  ) => {
    const contribFile = contribution.files.find(
      (cFile: any) => cFile.path === file.id,
    );
    const { gitHash } = contribFile.file;
    const contributionUuid = uuidV4();

    const contributionItems: ContributionItem[] = [
      {
        type: StatusType.Modified,
        path: file.id,
        fullPath: `${this.syncRepoManager.connectedPath}/${file.path}`,
        workingDirMeta: { hash: gitHash, size: null, mtime: null },
        hasUploadBeenInitiated: false,
        hasBeenStaged: false,
      },
    ];

    const repoRevisionString = contribFile.repoRevision
      ? `v${contribFile.repoRevision}`
      : '';
    const shortContribHash = contribution.version.substring(0, 7);
    const title = `Restored file ${file.filename} to previous revision ${repoRevisionString} in contribution #${shortContribHash}.`;

    const contributionState: ContributionState = {
      uuid: contributionUuid,
      items: contributionItems,
      title,
      description: '',
    };

    this.CancelPendingContribution();

    await this.DoContribute(contributionState);
  };
}
